import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert, AlertDescription } from '../components/ui/Alert';
import DOMPurify from 'dompurify';
import { addCsrfHeader } from '../utils/csrf';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Input sanitization and validation utilities
  const sanitizeText = (text) => {
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).trim();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    // Allow letters, spaces, hyphens, and apostrophes. Max 100 chars.
    const nameRegex = /^[a-zA-Z\s\-']{1,100}$/;
    return nameRegex.test(name) && name.trim().length >= 2;
  };

  const validateMessage = (message) => {
    const sanitized = sanitizeText(message);
    return sanitized.length >= 10 && sanitized.length <= 2000;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Comprehensive validation
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }

    // Validate and sanitize name
    if (!validateName(formData.name)) {
      setError('Please enter a valid name (2-100 characters, letters, spaces, hyphens, and apostrophes only).');
      return;
    }

    // Validate email
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate message
    if (!validateMessage(formData.message)) {
      setError('Please enter a message between 10 and 2000 characters.');
      return;
    }

    // Sanitize all input data
    const sanitizedData = {
      name: sanitizeText(formData.name),
      email: formData.email.toLowerCase().trim(), // Email sanitization
      subject: sanitizeText(formData.subject || ''),
      message: sanitizeText(formData.message)
    };

    // Submit sanitized form data to backend with CSRF protection
    try {
      const headers = await addCsrfHeader({
        'Content-Type': 'application/json',
      });

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session-based CSRF
        body: JSON.stringify(sanitizedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error('Contact form submission error:', err);
      setError(err.message || 'There was an error sending your message. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-surface dark:bg-surface-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-green-600 dark:text-green-400 text-6xl mb-4">✓</div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Thank You!
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Your message has been sent successfully. We'll get back to you as soon as possible.
              </p>
              <Button 
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ name: '', email: '', subject: '', message: '' });
                }}
                className="bg-gray-700 text-white hover:bg-gray-800"
              >
                Send Another Message
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface dark:bg-surface-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Have questions, feedback, or need support? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900 dark:text-white">Send us a Message</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you within 24 hours.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gray-700 text-white hover:bg-gray-800"
                >
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-gray-900 dark:text-white">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Email Support</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      <a href="mailto:support@vegetablah.com" className="hover:text-gray-900 dark:hover:text-white">
                        support@vegetablah.com
                      </a>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Response time: Within 24 hours
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-gray-900 dark:text-white">Useful Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Link to="/contact" className="font-semibold text-gray-900 dark:text-white hover:underline">
                      Contact Us
                    </Link>
                  </div>
                  <div>
                    <Link to="/terms-of-service" className="font-semibold text-gray-900 dark:text-white hover:underline">
                      Terms of Service
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-gray-900 dark:text-white">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">How do I reset my password?</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Use the "Forgot Password" link on the login page to reset your password via email.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Is my data secure?</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Yes, we use industry-standard encryption and security measures to protect your data.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Can I export my data?</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Yes, you can export your data in various formats from your profile settings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;